import type { EventEditFormValues } from '-/Event/EventEdit.types';
import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import capitalize from 'lodash.capitalize';
import { type ComponentRef, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import { useClient, useMutation, useQuery } from 'urql';
import { queueEventListReset } from '@/client/exchanges/cacheExchange/eventListResetQueue';
import { graphql } from '@/gql/graphql';
import { MAXIMUM_EVENT_TEAM_COUNT } from '@/utils/constants';
import eventTypeMap from '@/utils/eventTypeMap';
import getRelationshipRecords from '@/utils/getRelationshipRecords';
import isStandardGame from '@/utils/isStandardGame';
import setFalsyToNull from '@/utils/setFalsyToNull';
import ButtonToast, { useButtonToast } from '../Custom/ButtonToast';
import FormSaveButton from '../Custom/FormSaveButton';
import ModalKeyboardAwareScrollView from '../Custom/ModalKeyboardAwareScrollView';
import ModalKeyboardStickyView from '../Custom/ModalKeyboardStickyView';
import { useUploadEntityAvatars } from '../Entity/Entity.utils';
import type { EntityListField } from '../Entity/EntityList.types';
import Box from '../UI/Box';
import Card from '../UI/Card';
import EventEditInputDatetime from './EventEditInputDatetime';
import EventEditInputLocation from './EventEditInputLocation';
import EventEditInputNote from './EventEditInputNote';
import EventEditInputParent from './EventEditInputParent';
import EventEditInputStandings from './EventEditInputStandings';
import EventEditInputTitle from './EventEditInputTitle';
import EventEditTagGroup from './EventEditTagGroup';
import EventEditTeam from './EventEditTeam';
import EventUpdateDangerZone from './EventUpdateDangerZone';

const GetEventEditRootEventId = graphql(/* GraphQL */ `
  query GetEventEditRootEventId($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      root_parent_id
    }
  }
`);

const GetEventEditAccess = graphql(/* GraphQL */ `
  query GetEventEditAccess($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      permissions
    }
  }
`);
const UpdateEvent = graphql(/* GraphQL */ `
  mutation UpdateEvent(
    $eventId: uuid!
    $title: String
    $location: String
    $notes: String
    $datetime: timestamptz
    $homeTeamId: uuid
    $addedTeams: [event_team_insert_input!]!
    $removedTeams: [uuid!]
    $tagGroupId: uuid
    $hasStandings: Boolean
    $parentId: uuid
  ) {
    insert_event_team(objects: $addedTeams) {
      affected_rows
    }
    delete_event_team(
      where: { team_id: { _in: $removedTeams }, event_id: { _eq: $eventId } }
    ) {
      affected_rows
    }
    update_event_by_pk(
      pk_columns: { id: $eventId }
      _set: {
        title: $title
        location: $location
        notes: $notes
        datetime: $datetime
        home_team_id: $homeTeamId
        tag_group_id: $tagGroupId
        has_standings: $hasStandings
        parent_id: $parentId
      }
    ) {
      id
      title
      location
      datetime
      notes
      tag_group_id
      root_owner {
        id
        tag_group {
          id
        }
      }
    }
  }
`);
const InsertEvent = graphql(/* GraphQL */ `
  mutation InsertEvent(
    $title: String
    $location: String
    $notes: String
    $datetime: timestamptz
    $homeTeamId: uuid
    $eventTeams: [event_team_insert_input!]!
    $parentId: uuid
    $teamId: uuid
    $teamLimit: Int!
    $tagGroupId: uuid
    $hasStandings: Boolean
  ) {
    insert_event_one(
      object: {
        title: $title
        location: $location
        notes: $notes
        datetime: $datetime
        home_team_id: $homeTeamId
        event_teams: { data: $eventTeams }
        parent_id: $parentId
        team_id: $teamId
        tag_group_id: $tagGroupId
        has_standings: $hasStandings
      }
    ) {
      id
      title
      location
      datetime
      notes
      parent_id
      team_id
      event_type
      home_team_id
      tag_group_id
      has_standings
      event @_optional {
        id
        title
        parent_id
      }
      event_teams(
        limit: $teamLimit
        order_by: [{ is_home: asc }, { team: { title: asc } }]
      ) {
        event_id
        team_id
        team {
          id
          title
          avatar_id
          home_primary_color
        }
      }
      root_owner @_optional {
        id
        tag_group {
          id
        }
      }
    }
  }
`);
function getTeamInsert(
  team: EntityListField,
  rootEventId?: string | null,
  avatarId?: string | null,
) {
  return team.isNew
    ? {
        team: {
          data: {
            id: team.id,
            avatar_id: avatarId,
            home_primary_color: team.color,
            title: team.title,
            root_event_id: rootEventId,
          },
        },
      }
    : {
        team_id: team.id,
      };
}
function shouldShowEventSection(
  section: keyof EventEditFormValues,
  eventType?: EventEditFormValues['eventType'] | null,
) {
  return eventType
    ? eventTypeMap[eventType].activeFields.includes(section)
    : false;
}
function EventEdit() {
  const [, insertEvent] = useMutation(InsertEvent);
  const [, updateEvent] = useMutation(UpdateEvent);
  const { upload: uploadEntityAvatars } = useUploadEntityAvatars('team');
  const client = useClient();
  const { eventId } = useLocalSearchParams<{
    eventId?: string;
  }>();
  const scrollRef =
    useRef<ComponentRef<typeof ModalKeyboardAwareScrollView>>(null);
  const {
    handleSubmit,
    formState: { defaultValues, isLoading: isFormLoading },
    reset,
    trigger,
    control,
  } = useFormContext<EventEditFormValues>();
  const [toast, setToast] = useButtonToast();
  const { back, replace } = useRouter();
  const eventType = useWatch({
    name: 'eventType',
    control,
  });
  const title = useWatch({
    name: 'title',
    control,
  });
  const [{ data: accessData, fetching: accessFetching }] = useQuery({
    query: GetEventEditAccess,
    variables: { eventId: eventId || '' },
    pause: !eventId,
  });
  const canEdit = !eventId || !!accessData?.event_by_pk?.permissions?.edit;
  const isLoading = isFormLoading || (!!eventId && accessFetching);
  const onPressSave = () => {
    handleSubmit(async (formValues) => {
      let rootEventId: string | null | undefined;
      let newTeamAvatars: {
        [key: string]: string;
      } = {};
      if (formValues.parentId && formValues.teams?.length) {
        const { data: rootEventData } = await client.query(
          GetEventEditRootEventId,
          {
            eventId: formValues.parentId,
          },
        );
        rootEventId = rootEventData?.event_by_pk?.root_parent_id;
      }
      const newTeamsWithAvatar =
        formValues.teams?.filter((team) => team.isNew && team.localAvatarUrl) ||
        [];
      if (newTeamsWithAvatar?.length) {
        try {
          newTeamAvatars = await uploadEntityAvatars(newTeamsWithAvatar);
        } catch (error) {
          if (error instanceof Error) {
            setToast({
              text: error.message,
              variant: 'error',
              icon: 'close-outline',
            });
            return;
          }
        }
      }
      const { parentId, datetime, teams, eventType } = formValues;
      const isGame = isStandardGame(eventType, teams?.length);
      if (!eventId && eventType) {
        const selectedTeams =
          formValues.teams?.map((selectedTeam) =>
            getTeamInsert(
              selectedTeam,
              rootEventId,
              selectedTeam.id ? newTeamAvatars[selectedTeam.id] : null,
            ),
          ) || [];
        const formattedValues = setFalsyToNull({
          ...formValues,
          eventTeams: selectedTeams,
          datetime: eventType === 'game' ? datetime : null,
          parentId,
          homeTeamId: isGame ? teams?.[1]?.id : null,
          teamLimit: MAXIMUM_EVENT_TEAM_COUNT,
          hasStandings: !!formValues.hasStandings,
        });
        const { data: insertEventData } = await insertEvent(formattedValues);
        if (insertEventData?.insert_event_one) {
          queueEventListReset({
            datetime: insertEventData.insert_event_one.datetime,
            parentEventId: parentId,
          });
          if (['organization', 'group'].includes(eventType)) {
            reset({
              ...defaultValues,
              teams: formValues.teams?.map((team) => ({
                ...team,
                avatarId: team.id
                  ? newTeamAvatars[team.id] || team.avatarId
                  : null,
                isNew: false,
              })),
            });
            replace({
              pathname: '/event/create/[eventId]',
              params: {
                eventId: insertEventData.insert_event_one.id,
              },
            });
          } else {
            setToast({
              text: `Created ${capitalize(eventType)}`,
              variant: 'success',
              icon: 'checkmark',
            });
            reset({
              ...defaultValues,
              datetime,
              parentId,
            });
          }
        } else {
          setToast({
            text: 'Something Went Wrong',
            variant: 'error',
            icon: 'close',
          });
        }
      } else if (eventId) {
        const oldTeams =
          defaultValues?.teams?.flatMap((team) => (team?.id ? [team] : [])) ||
          [];
        const newTeams = teams || [];
        const { added: addedTeams, removed: removedTeams } =
          getRelationshipRecords<EntityListField, any>(
            oldTeams,
            newTeams,
            (item) => ({
              event_id: eventId,
              ...getTeamInsert(
                item,
                rootEventId,
                item.id ? newTeamAvatars[item.id] : null,
              ),
            }),
            'id',
          );
        const formattedValues = setFalsyToNull({
          eventId,
          ...formValues,
          addedTeams,
          removedTeams: removedTeams.flatMap((team) =>
            team.id ? [team.id] : [],
          ),
          homeTeamId: isGame ? teams?.[1]?.id : null,
          parentId,
          hasStandings: !!formValues.hasStandings,
        });
        const { error } = await updateEvent(formattedValues);
        if (!error) {
          back();
        } else {
          setToast({
            text: 'Something Went Wrong',
            variant: 'error',
            icon: 'close',
          });
        }
      }
      scrollRef.current?.scrollTo({
        y: 0,
      });
    })();
  };

  const triggerCallback = () => {
    if (isLoading) {
      return;
    }
    trigger();
  };
  useFocusEffect(triggerCallback);
  if (isLoading) {
    return (
      <>
        <Stack.Screen
          options={{
            title: '',
          }}
        />
        <Box flex style={style.loadingSurface} />
      </>
    );
  }
  return (
    <>
      <Stack.Screen
        options={{
          title:
            title ||
            `${eventId ? 'Edit' : 'New'} ${eventTypeMap[eventType || 'game'].label}`,
        }}
      />
      <ModalKeyboardAwareScrollView ref={scrollRef}>
        <Box direction="vertical" space="sm" p="md">
          {canEdit && shouldShowEventSection('parentId', eventType) ? (
            <Card>
              <EventEditInputParent />
            </Card>
          ) : null}

          {canEdit ? (
            <Box space="sm">
              {shouldShowEventSection('datetime', eventType) ? (
                <EventEditInputDatetime />
              ) : null}

              {shouldShowEventSection('hasStandings', eventType) ? (
                <EventEditInputStandings />
              ) : null}

              {shouldShowEventSection('title', eventType) ? (
                <EventEditInputTitle />
              ) : null}

              {shouldShowEventSection('location', eventType) ? (
                <EventEditInputLocation />
              ) : null}

              {shouldShowEventSection('notes', eventType) ? (
                <EventEditInputNote />
              ) : null}

              {shouldShowEventSection('tagGroupId', eventType) ? (
                <EventEditTagGroup />
              ) : null}
            </Box>
          ) : null}

          {canEdit && shouldShowEventSection('teams', eventType) ? (
            <Card>
              <EventEditTeam />
            </Card>
          ) : null}

          {eventId ? <EventUpdateDangerZone /> : null}
        </Box>
      </ModalKeyboardAwareScrollView>
      {canEdit ? (
        <ModalKeyboardStickyView>
          <ButtonToast {...toast} />
          <FormSaveButton info={{ text: 'Save' }} onPress={onPressSave} />
        </ModalKeyboardStickyView>
      ) : null}
    </>
  );
}
const style = StyleSheet.create((theme) => ({
  loadingSurface: {
    backgroundColor: theme.colors.background,
  },
}));
export default EventEdit;
